import { getFirebaseIdToken } from "@/shared/integrations/firebase/client";
import { getClientProjectChatUrl } from "@/shared/lib/intelligenceChatRouting";
import { captureEdgeFailure } from "@/shared/integrations/sentry/reportEdgeFailure";
import { SUPABASE_PUBLISHABLE_KEY } from "@/shared/integrations/supabase/client";

export type IntelAnswerShape = "scalar" | "table" | "prose" | "mixed";

/** A1 `final_meta` frame — engine-authored answer-shape facts. Emitted once,
 *  just before the final answer text, only because we send `include_final_meta`. */
export interface IntelFinalMeta {
  assembled: boolean;
  answer_shape: IntelAnswerShape;
  provenance_sets: { qtag: string; cited_cells: number }[];
  numbers_rendered: number;
  truncated_sets: string[];
}

/** Typed column semantics from the contract-v2 wire (intel ticket 36):
 *  `type` is the DDL type (text|uuid|numeric|integer|boolean|date|…), `kind`
 *  the semantic role (semantic|identifier|enum|…). Presentation decisions
 *  prefer these over name heuristics when present (46 amendment). */
export interface IntelColumnMeta {
  name: string;
  type?: string;
  kind?: string;
  unit?: string;
}

/** One verbatim result set from the A2 `result_sets` frame (already
 *  scrubbed server-side; `rows` align positionally with `columns`).
 *  Under the emit-all contract ALL retained sets arrive, cited or not. */
export interface IntelResultSet {
  qtag: string;
  columns: string[];
  rows: (string | number | boolean | null)[][];
  shown_row_count: number;
  total_row_count: number;
  truncated: boolean;
  /** True iff this set's qtag appears in the answer's provenance (emit-all
   *  wire contract). Pre-emit-all servers omit the flag and emit only cited
   *  sets — treat missing as cited. */
  cited?: boolean;
  /** Per-set semantics (intel ticket 29). All optional: v56-and-earlier
   *  servers omit them and the card degrades to the derived/ordinal title
   *  with no query affordance. */
  /** Model-authored short title ("what this query is for"), scrubbed
   *  server-side; the server derives a fallback so ordinal-only sets should
   *  never arrive from a post-29 deploy. */
  label?: string;
  /** Model-marked scaffolding/probe query, deterministically guarded
   *  server-side: a cited set never arrives internal, and an all-internal
   *  run arrives with no internal flags at all. Under contract v2 this is
   *  DERIVED from `role` (work|debug ⇒ true) as a transition bridge. */
  internal?: boolean;
  /** Contract-v2 semantic bundle (intel ticket 36) — present only when the
   *  server honors the `contract_v2` request flag; all optional so v1 wires
   *  degrade byte-identically. `role` is emit-time guarded (a cited set is
   *  never work/debug). `template_id` is non-null iff the set came from
   *  run_canonical_template — the Canonical-query badge condition (35 Q10). */
  role?: "answer" | "support" | "work" | "debug";
  template_id?: string | null;
  columns_meta?: IntelColumnMeta[];
}

/** A set the byte budget dropped: its presence survives as a wire stub even
 *  though its rows don't (contract v2 — deterministic degradation). */
export interface IntelDroppedSetStub {
  qtag: string;
  label: string;
  total_row_count: number;
  reason: string;
}

/** Frame-level facts of one `result_sets` frame beyond the sets themselves. */
export interface IntelResultSetsInfo {
  /** Sets silently dropped past the ~100KB budget (intel ticket 29 adds the
   *  count; pre-29 servers omit it → 0). */
  setsDropped: number;
  /** Contract-v2 stubs for the dropped sets (ticket 36); v1 wires omit them. */
  droppedSets?: IntelDroppedSetStub[];
}

/** Contract-v2 chart proposal (intel ticket 37; prototype-ratified shape).
 *  Structurally identical to chartRef.ts's ChartRef — the engine re-validates
 *  and binds the data client-side, the model never authors chart data. */
export interface IntelChartProposal {
  kind: "bar" | "donut" | "bar_list" | "kpi_row";
  x?: string;
  y?: string[];
  label?: string;
}

/** Contract-v2 validated table view (intel ticket 37): only server-validated
 *  views reach the wire, so every named column exists on the set. */
export interface IntelTableView {
  columns?: string[];
  sort?: { by: string; dir: "asc" | "desc" };
  limit?: number;
}

/** One rendered block from the contract-v2 `answer_blocks` frame (ticket 38).
 *  Claim/caveat `text` is engine-filled and scrubbed — render verbatim, never
 *  re-derive digits. Named IntelWireBlock to avoid colliding with IntelChat's
 *  markdown-parse AnswerBlock type. */
export type IntelWireBlock =
  | { kind: "claim"; text: string; evidence: string[] }
  | { kind: "caveat"; text: string }
  | { kind: "table_ref"; qtag: string; view?: IntelTableView; view_note?: string }
  | { kind: "chart_ref"; qtag: string; proposal: IntelChartProposal }
  | { kind: "chart_ref"; qtag: string; proposal: null; fallback_reason: string };

/** Contract-v2 `diagnostics` frame — assembly honesty, always emitted under
 *  v2 (absence means a v1 wire or a non-v2 escape path, never a semantic
 *  state). */
export interface IntelDiagnostics {
  assembly: "full" | "partial" | "backstop";
  degraded_reason?: string;
  dropped_result_sets: IntelDroppedSetStub[];
  warnings: string[];
}

export interface IntelStreamHandlers {
  /** Interim status line — the only pre-final signal (the final answer is
   *  buffered server-side, not token-streamed). */
  onProgress: (label: string) => void;
  /** Accumulated answer text so far. */
  onAnswerText: (fullText: string) => void;
  onFinalMeta: (meta: IntelFinalMeta) => void;
  onResultSets: (sets: IntelResultSet[], info: IntelResultSetsInfo) => void;
  /** Contract-v2 frames (ticket 38) — optional so existing callers and v1
   *  wires (which never emit them) need no changes. */
  onAnswerBlocks?: (blocks: IntelWireBlock[]) => void;
  onDiagnostics?: (diagnostics: IntelDiagnostics) => void;
}

const ENDPOINT = "client-project-chat-prod-port";

/**
 * One submission → one intel-chat run. Sends a SINGLE user message (each
 * submission is independent — no multi-turn v1) with both I1 flags set, and
 * dispatches the SSE frames to the handlers. Resolves when the stream ends;
 * rejects on auth/HTTP/network failure or abort.
 *
 * SSE reader mechanics salvaged from the retired Dashboard2Chat client.
 */
export async function streamIntelChat({
  clientId,
  question,
  signal,
  handlers,
}: {
  clientId: string;
  question: string;
  signal?: AbortSignal;
  handlers: IntelStreamHandlers;
}): Promise<void> {
  const idToken = await getFirebaseIdToken();
  if (!idToken) throw new Error("Please sign in to use the chat.");

  const resp = await fetch(getClientProjectChatUrl(), {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      clientId,
      messages: [{ role: "user", content: question }],
      include_final_meta: true,
      include_result_tables: true,
      // Contract-v2 opt-in (ticket 38): pre-v2 servers ignore unknown request
      // fields, so this is inert until the consolidated intel PR deploys.
      contract_v2: true,
    }),
  });

  if (!resp.ok) {
    captureEdgeFailure({ endpoint: ENDPOINT, status: resp.status });
    throw new Error(
      resp.status === 429
        ? "Rate limited — please wait a moment and try again."
        : `The intelligence agent is unavailable right now (HTTP ${resp.status}).`,
    );
  }
  if (!resp.body) throw new Error("No response body");

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let textBuffer = "";
  let streamDone = false;
  let answerText = "";

  while (!streamDone) {
    const { done, value } = await reader.read();
    if (done) break;
    textBuffer += decoder.decode(value, { stream: true });

    let newlineIndex: number;
    while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
      let line = textBuffer.slice(0, newlineIndex);
      textBuffer = textBuffer.slice(newlineIndex + 1);

      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;

      const jsonStr = line.slice(6).trim();
      if (jsonStr === "[DONE]") {
        streamDone = true;
        break;
      }

      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(jsonStr);
      } catch {
        // A COMPLETE line that isn't valid JSON. Frames are guaranteed
        // single-line JSON server-side (ndjsonSafeStringify), and a partial
        // line never reaches here (no trailing \n yet) — so this is genuine
        // corruption. Skip the line; re-buffering it (the old Dashboard2Chat
        // behavior) would stall the loop and silently drop the rest of the run.
        continue;
      }

      if (parsed.type === "progress" && typeof parsed.label === "string") {
        handlers.onProgress(parsed.label);
      } else if (parsed.type === "final_meta") {
        handlers.onFinalMeta(parsed as unknown as IntelFinalMeta);
      } else if (parsed.type === "result_sets" && Array.isArray(parsed.sets)) {
        handlers.onResultSets(parsed.sets as IntelResultSet[], {
          setsDropped: typeof parsed.sets_dropped === "number" ? parsed.sets_dropped : 0,
          ...(Array.isArray(parsed.dropped_sets)
            ? { droppedSets: parsed.dropped_sets as IntelDroppedSetStub[] }
            : {}),
        });
      } else if (parsed.type === "answer_blocks" && Array.isArray(parsed.blocks)) {
        handlers.onAnswerBlocks?.(parsed.blocks as IntelWireBlock[]);
      } else if (parsed.type === "diagnostics") {
        handlers.onDiagnostics?.(parsed as unknown as IntelDiagnostics);
      }

      if (parsed.done) {
        streamDone = true;
        break;
      }

      const content = (
        parsed as { choices?: { delta?: { content?: string } }[] }
      ).choices?.[0]?.delta?.content;
      if (typeof content === "string" && content) {
        answerText += content;
        handlers.onAnswerText(answerText);
      }
    }
  }
}
